import server from 'api';

// Mirrors gastronaut-api serverV03/Routes/ManagedWebsites (views.ts, router.ts).

const BASE = '/v03/managed-websites';
const data = <T>(response: { data: T }) => response.data;

export type DraftTarget = { kind: 'live' } | { kind: 'request'; changeRequestId: string };

export type TextEdit = {
  id: string;
  kind: 'text';
  fieldId: string;
  page: string;
  value: string;
  previous: string | null;
};

export type ImageEdit = {
  id: string;
  kind: 'image';
  fieldId: string;
  page: string;
  mediaId: string;
  alt: string | null;
  previousUrl: string | null;
};

/** A new target of a link (its label is a text of its own): a page of the website or an http(s) address. */
export type LinkEdit = {
  id: string;
  kind: 'link';
  fieldId: string;
  page: string;
  href: string;
  previous: string | null;
};

/** A new background video; `alsoFieldIds` are the video's other sources, which show the same upload. */
export type VideoEdit = {
  id: string;
  kind: 'video';
  fieldId: string;
  page: string;
  mediaId: string;
  alsoFieldIds: string[];
  previousUrl: string | null;
};

export type CommentAnchor = {
  selector: string | null;
  fieldId: string | null;
  text: string | null;
  x: number | null;
  y: number | null;
};

export type CommentItem = {
  id: string;
  kind: 'comment';
  page: string;
  anchor: CommentAnchor | null;
  text: string;
  mediaIds: string[];
};

export type StructuralAction =
  | 'create_page'
  | 'duplicate_page'
  | 'delete_page'
  | 'create_event'
  | 'duplicate_event'
  | 'delete_event';

export type StructuralItem = {
  id: string;
  kind: 'structural';
  action: StructuralAction;
  page: string | null;
  text: string;
};

export type AnswerItem = { id: string; kind: 'answer'; questionId: string; text: string };

export type DraftItem = TextEdit | ImageEdit | LinkEdit | VideoEdit | CommentItem | StructuralItem | AnswerItem;

/** Edits of one field (all others are wishes, comments or answers). */
export type FieldEdit = TextEdit | ImageEdit | LinkEdit | VideoEdit;

export type Draft = {
  websiteId: string;
  uid: string;
  target: DraftTarget;
  items: DraftItem[];
  // Edits of a failed direct publication whose fields were changed again.
  recovered?: (DraftItem & { failedPackageId: string })[];
  revision: number;
  updatedAt: number;
};

export type RequestState =
  | 'queued'
  | 'preparing'
  | 'needs_answer'
  | 'needs_review'
  | 'preview_ready'
  | 'publishing'
  | 'published'
  | 'needs_team'
  | 'cancelled';

export type RequestView = {
  changeRequestId: string;
  title: string;
  state: RequestState;
  structural: boolean;
  approved: boolean;
  preview: { version: string; url: string; summary: string; readyAt: number; outdated: boolean } | null;
  question: { questionId: string; text: string } | null;
  problem: string | null;
  pendingFeedback: number;
  waitingForWorker: boolean;
  createdAt: number;
  updatedAt: number;
};

export type DirectChangeView = {
  jobId: string;
  kind: 'direct_publish' | 'restore';
  state: 'queued' | 'leased' | 'observing' | 'succeeded' | 'failed' | 'needs_team' | 'cancelled' | 'paused';
  summary: string;
  problem: string | null;
  waitingForWorker: boolean;
  createdAt: number;
  updatedAt: number;
};

export type PublicationView = {
  publicationId: string;
  kind: 'initial' | 'direct' | 'request' | 'restore';
  summary: string;
  at: number;
  url: string | null;
  live: boolean;
};

export type WebsiteOverview = {
  website: {
    websiteId: string;
    name: string;
    status: 'active' | 'paused';
    url: string;
    editorUrl?: string | null;
    liveRevision: number | null;
    publishedAt: number | null;
    ready: boolean;
    publishing: boolean;
  };
  workerOnline: boolean;
  myLiveDraft: Draft | null;
  openRequests: RequestView[];
  closedRequests: RequestView[];
  directChanges: DirectChangeView[];
  history: PublicationView[];
};

export type WebsiteSummary = { websiteId: string; name: string; status: 'active' | 'paused'; url: string };

export type MediaView = {
  mediaId: string;
  status: 'processing' | 'ready' | 'failed';
  fileName: string;
  problem: string | null;
  /**
   * `url` opens only until `linksExpireAt` (the upload is private). An image
   * has optimised versions; a video one version, the uploaded file itself
   * (format mp4 or webm, never re-encoded).
   */
  variants: { name: string; width: number; height: number; format: string; url: string; sitePath: string }[];
  linksExpireAt: number;
  meta: { width: number; height: number; hasAlpha: boolean; kind: 'photo' | 'graphic' } | null;
};

const targetParam = (target: DraftTarget) => (target.kind === 'live' ? 'live' : target.changeRequestId);

export const fetchWebsites = (restaurantId: string): Promise<{ websites: WebsiteSummary[] }> =>
  server.get(`${BASE}/${restaurantId}`).then(data);

export const fetchOverview = (restaurantId: string, websiteId: string): Promise<WebsiteOverview> =>
  server.get(`${BASE}/${restaurantId}/${websiteId}`).then(data);

export const fetchDraft = (restaurantId: string, websiteId: string, target: DraftTarget): Promise<{ draft: Draft }> =>
  server.get(`${BASE}/${restaurantId}/${websiteId}/draft`, { params: { target: targetParam(target) } }).then(data);

/**
 * What an editor showed when it opened: the live website at a revision or one
 * exact preview version. The server checks it and keeps it as the base of the
 * draft's text and image edits until the draft is sent or emptied.
 */
export type BaseClaim = { liveRevision: number } | { previewVersion: string };

export const saveDraft = (
  restaurantId: string,
  websiteId: string,
  target: DraftTarget,
  items: DraftItem[],
  expectedRevision: number,
  base?: BaseClaim
): Promise<{ draft: Draft }> =>
  server
    .put(`${BASE}/${restaurantId}/${websiteId}/draft`, { target: targetParam(target), items, expectedRevision, ...(base ? { base } : {}) })
    .then(data);

export const resolveRecovered = (
  restaurantId: string,
  websiteId: string,
  body: { itemId: string; action: 'adopt' | 'discard'; expectedRevision: number }
): Promise<{ draft: Draft }> =>
  server.post(`${BASE}/${restaurantId}/${websiteId}/draft/recovered`, body).then(data);

export const submitDraft = (
  restaurantId: string,
  websiteId: string,
  target: DraftTarget,
  body: { draftRevision: number; submissionId: string; intent: 'publish' | 'request' }
): Promise<{ packageId: string; route: 'direct' | 'request'; changeRequestId: string | null; jobId: string | null }> =>
  server
    .post(`${BASE}/${restaurantId}/${websiteId}/submit`, { target: targetParam(target), ...body })
    .then(data);

export const fetchRequest = (
  restaurantId: string,
  websiteId: string,
  changeRequestId: string
): Promise<{ request: RequestView; myDraft: Draft | null }> =>
  server.get(`${BASE}/${restaurantId}/${websiteId}/requests/${changeRequestId}`).then(data);

export const publishRequest = (
  restaurantId: string,
  websiteId: string,
  changeRequestId: string,
  previewVersion: string
): Promise<{ jobId: string; state: string }> =>
  server
    .post(`${BASE}/${restaurantId}/${websiteId}/requests/${changeRequestId}/publish`, { previewVersion })
    .then(data);

export const restorePublication = (
  restaurantId: string,
  websiteId: string,
  publicationId: string,
  expectedLiveRevision: number
): Promise<{ jobId: string }> =>
  server
    .post(`${BASE}/${restaurantId}/${websiteId}/restore`, { publicationId, expectedLiveRevision, confirm: true })
    .then(data);

export const uploadMedia = (restaurantId: string, websiteId: string, file: File): Promise<{ media: MediaView }> =>
  server
    .post(`${BASE}/${restaurantId}/${websiteId}/media`, file, {
      headers: { 'content-type': file.type, 'x-file-name': encodeURIComponent(file.name) },
      timeout: 120000,
    })
    .then(data);

export const fetchMedia = (restaurantId: string, websiteId: string, mediaId: string): Promise<{ media: MediaView }> =>
  server.get(`${BASE}/${restaurantId}/${websiteId}/media/${mediaId}`).then(data);

/** Status and machine-readable code of a failed call, for the screen's own wording. */
export const websiteApiError = (error: any) => ({
  status: (error?.response?.status as number | undefined) ?? 0,
  code: (error?.response?.data?.code as string | undefined) ?? null,
  draft: (error?.response?.data?.draft as Draft | null | undefined) ?? null,
});
